import type { LucideIcon } from 'lucide-react';
import {
  FileText,
  LayoutGrid,
  Network,
  Package,
  Receipt,
  SatelliteDish,
  Settings,
  Users,
  Wifi,
} from 'lucide-react';

export enum AccountDetailsTab {
  DASHBOARD = 'Dashboard',
  SUBSCRIPTIONS = 'Subscriptions',
  TERMINALS = 'Terminals',
  L2VPN_CIRCUITS = 'L2VPN Circuits',
  ROUTER_CONFIGS = 'Router Configs',
  CUSTOMERS = 'Customers',
  AUDIT_LOGS = 'Audit Logs',
  INVOICES = 'Invoices',
  SETTINGS = 'Settings',
}

export interface AccountDetailsTabConfig {
  tab: AccountDetailsTab;
  slug: string;
  icon: LucideIcon;
  /** Visible only to platform super-admins (Audit Logs, Invoices). */
  adminOnly?: boolean;
}

/** Single source of truth for account detail tabs (slug, enum, icon, visibility). */
export const ACCOUNT_DETAILS_TAB_CONFIG: readonly AccountDetailsTabConfig[] = [
  { tab: AccountDetailsTab.DASHBOARD, slug: 'dashboard', icon: LayoutGrid },
  { tab: AccountDetailsTab.TERMINALS, slug: 'terminals', icon: SatelliteDish },
  { tab: AccountDetailsTab.SUBSCRIPTIONS, slug: 'subscriptions', icon: Package },
  { tab: AccountDetailsTab.L2VPN_CIRCUITS, slug: 'l2vpn-circuits', icon: Network },
  { tab: AccountDetailsTab.ROUTER_CONFIGS, slug: 'router-configs', icon: Wifi, adminOnly: true },
  { tab: AccountDetailsTab.CUSTOMERS, slug: 'customers', icon: Users },
  { tab: AccountDetailsTab.AUDIT_LOGS, slug: 'audit-logs', icon: FileText, adminOnly: true },
  { tab: AccountDetailsTab.INVOICES, slug: 'invoices', icon: Receipt, adminOnly: true },
  { tab: AccountDetailsTab.SETTINGS, slug: 'settings', icon: Settings },
] as const;

const TAB_TO_SLUG = Object.fromEntries(
  ACCOUNT_DETAILS_TAB_CONFIG.map(({ tab, slug }) => [tab, slug]),
) as Record<AccountDetailsTab, string>;

const SLUG_TO_TAB = Object.fromEntries(
  ACCOUNT_DETAILS_TAB_CONFIG.map(({ tab, slug }) => [slug, tab]),
) as Record<string, AccountDetailsTab>;

export const ACCOUNT_DETAILS_TAB_SLUGS = ACCOUNT_DETAILS_TAB_CONFIG.map((c) => c.slug);

export function getTabSlug(tab?: AccountDetailsTab | undefined): string {
  if (!tab) return TAB_TO_SLUG[AccountDetailsTab.DASHBOARD];
  return TAB_TO_SLUG[tab] ?? TAB_TO_SLUG[AccountDetailsTab.DASHBOARD];
}

export function getTabFromSlug(slug?: string | undefined): AccountDetailsTab {
  if (!slug) return AccountDetailsTab.DASHBOARD;
  return SLUG_TO_TAB[slug] ?? SLUG_TO_TAB[AccountDetailsTab.DASHBOARD];
}

export function isValidAccountDetailsTabSlug(slug: string): boolean {
  return slug in SLUG_TO_TAB;
}

export function getVisibleAccountDetailsTabs(isPlatformUser: boolean): AccountDetailsTabConfig[] {
  return ACCOUNT_DETAILS_TAB_CONFIG.filter((config) => !config.adminOnly || isPlatformUser);
}
